import update from 'immutability-helper';
import { uniqueId } from 'lodash';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Field, useField } from 'formik';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import { formatBytes } from '@dashboard/services/formatting';
import logException from '@dashboard/services/logException';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import { confirm } from '@dashboard/lib/confirm';

import Button from '@dashboard/components/Button';
import Reorderable from '@dashboard/components/Reorderable';
import clsx from 'clsx';
import Tooltip from '../../Tooltip';

function filesReducer(state, action) {
  switch (action.type) {
    case 'set':
      return update(state, { $set: action.values });
    case 'add':
      if (state && state.length > 0) {
        return update(state, { $push: action.values });
      } else {
        return action.values;
      }
    case 'update': {
      let index;
      if (action.file._id) {
        index = state.findIndex(f => f._id === action.file._id);
      } else {
        index = state.findIndex(f => f.key === action.file.key);
      }

      if (index >= 0) {
        return update(state, {
          [index]: {
            $set: action.file,
          },
        });
      } else {
        return update(state, {
          $push: [action.file],
        });
      }
    }
    case 'delete': {
      if (action.file._id) {
        return state.filter(f => f._id !== action.file._id);
      } else {
        return state.filter(f => f.key !== action.file.key);
      }
    }
    default:
      return state;
  }
}

const Images = ({ media = [], onUpdate }) => {
  const { sellerId } = useAuth();
  const [files, dispatchFiles] = useReducer(filesReducer, media);
  const { uploadFile, deleteFile } = useFileQueries();

  useEffect(() => {
    const updatedFiles = files.filter(file => !file.isUploading);
    // check if the number of files has changed before updating
    // this is to prevent the form from updating due to uploadProgress changes
    if (updatedFiles.length !== media.length) {
      onUpdate(updatedFiles);
    }
  }, [files]);

  const onDrop = useCallback(
    async acceptedFiles => {
      const acceptedFilesToSet = acceptedFiles.map(file =>
        Object.assign(file, {
          key: uniqueId(),
          url: URL.createObjectURL(file),
          isUploading: true,
          isUploaded: false,
        }),
      );

      dispatchFiles({ type: 'add', values: acceptedFilesToSet });

      async function upload(file) {
        try {
          const { data, error } = await uploadFile({
            file,
            bucket: 'images',
            sellerId,
            onProgress: progress => {
              dispatchFiles({
                type: 'update',
                file: { ...file, name: file.name || data.name, uploadProgress: progress },
              });
            },
          });

          if (error) {
            throw error;
          }

          const mediaItem = {
            key: file.key,
            file: data._id,
            url: data.location,
            name: data.name,
            size: data.size,
            width: data.width,
            height: data.height,
          };

          dispatchFiles({
            type: 'update',
            file: mediaItem,
          });
        } catch (error) {
          logException({ message: 'Failed to upload file', error });
          dispatchFiles({
            type: 'delete',
            file,
          });
        }
      }

      await Promise.all(
        acceptedFilesToSet.map(async file => {
          let img = new Image();
          img.src = window.URL.createObjectURL(file);
          img.onload = () => {
            file.width = img.width;
            file.height = img.height;
          };

          await upload(file);
        }),
      );
    },
    [files],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png, image/jpg, image/gif, image/webp, image/svg+xml',
    maxSize: 10000000,
  });

  async function onDeleteFile(file, fileId) {
    if (fileId) {
      await deleteFile(fileId);
    }

    dispatchFiles({ type: 'delete', file });
  }

  return (
    <div>
      {files.length > 0 && (
        <div className="grid grid-cols-1 gap-x-3 w-full border border-gray-200 rounded my-3">
          <Reorderable
            id="media"
            items={files}
            onReorder={items => {
              dispatchFiles({
                type: 'set',
                values: items,
              });

              onUpdate(items);
            }}
            renderItem={({ item: file, index, dragHandleProps }) => (
              <div {...dragHandleProps}>
                <File
                  file={file}
                  onDeleteFile={() => onDeleteFile(file, file.file)}
                  isLast={index + 1 === files.length}
                />
              </div>
            )}
          />
        </div>
      )}

      <div
        {...getRootProps()}
        className={`transition-all p-3 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:border-chocolate-600 focus:border-solid hover:border-chocolate-1000 hover:border-solid ${
          isDragActive ? 'border-chocolate-600 border-solid' : 'border-dashed'
        }`}
      >
        <input name="media" id="media" {...getInputProps()} />

        <div className="flex justify-start">
          <div className="mr-2">
            <svg
              className="h-10 w-10 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="">
            <div className="flex text-gray-600">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-chocolate-600 hover:text-chocolate-600 focus-within:outline-none"
              >
                <span>Upload images</span>
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-sm text-gray-500">Recommended dimensions: 1280 x 720 (4:3)</p>
          </div>
        </div>
      </div>

      {fileRejections.map(({ file, errors }) => (
        <FileRejection key={file.path} file={file} errors={errors} />
      ))}
    </div>
  );
};

function FileRejection({ file, errors }) {
  if (!file.type.startsWith('image/')) {
    return (
      <div className="my-1 text-red-600" key={file.path}>
        {file.name} is not an image
      </div>
    );
  }

  if (file.size > 10000000) {
    return (
      <div className="my-1 text-red-600">
        {`${file.name} is too big (${formatBytes(file.size)}). Max size: 10MB`}
      </div>
    );
  }

  return errors.map(err => (
    <div key={err.key} className="my-1 text-red-600">
      {err.message}
    </div>
  ));
}

function File({ file, onDeleteFile, isLast }) {
  const [showProgress, setShowProgress] = useState(file.isUploading);

  useEffect(() => {
    if (file.isUploading) {
      setShowProgress(true);
    } else {
      const timer = setTimeout(() => setShowProgress(false), 500);
      return () => clearTimeout(timer);
    }
  }, [file.isUploading]);

  async function onDelete() {
    const del = await confirm('Delete this image?', {
      confirm: 'Delete',
    });

    if (del) {
      try {
        await onDeleteFile(file);
      } catch (error) {
        window.alert('Unable to delete image');
      }
    }
  }

  const fileImage = (() => {
    if (file.url.includes('blob:')) {
      return { src: file.url, srcSet: undefined };
    }

    return getImageSrcAndSrcSet({ url: file.url, width: 80, height: 64 });
  })();

  return (
    <div className={`${!isLast && 'border-gray-200 border-b'}`}>
      <div className={`flex flex-row gap-3 p-4 items-center justify-start`}>
        <div className="flex-grow-0 flex-shrink-0 overflow-hidden">
          <img
            className={clsx('w-20 h-16 object-cover text-transparent', {
              'opacity-50 transition-opacity': file.isUploading,
            })}
            src={fileImage.src}
            srcSet={fileImage.srcSet}
            alt={file.name}
          />
        </div>

        <div className="overflow-hidden">
          <div className="font-sm truncate">{file.name}</div>
        </div>

        <div className="flex ml-auto gap-2 items-center">
          <div className="flex gap-2">
            <Tooltip label="Delete">
              <Button appearance="subtle" onClick={onDelete} aria-label="delete" type="button">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  height="16px"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      {showProgress && (
        <div className="relative w-full h-1 bg-gray-200 rounded">
          <div
            className="absolute h-full bg-chocolate-1000 rounded transition-all duration-300"
            style={{
              width: `${
                file.uploadProgress == null || file.uploadProgress > 10 ? file.uploadProgress : 1
              }%`,
            }}
          />
        </div>
      )}
    </div>
  );
}

export const ImagesField = props => {
  // eslint-disable-next-line no-unused-vars
  const [field, meta, helpers] = useField(props);

  const { setValue } = helpers;

  const onChange = value => {
    setValue(value);

    if (props.onChange) {
      props.onChange(value);
    }
  };

  return <Field {...props} as={Images} onUpdate={onChange} media={field.value} />;
};

export default Images;
